import { Entity, EntityBase, Var } from "addeus-common-library/stores/firestore";
import { Collection } from "addeus-common-library/stores/firestore/collection";
import moment from "moment-with-locales-es6";
import {
    AIStats,
    CommunityAudit,
    ProfileAudit,
    PublicationsAudit,
    SummaryAudit,
} from "./aiDatas";
import { MediaAudit } from "./post";

export enum AuditCreatingStep {
    Start = "Start",
    Profile = "Profile",
    Publications = "Publications",
    Community = "Community",
    Completion = "Completion",
    Completed = "Completed",
}

export class ConversationUser extends EntityBase {
    @Var(String)
    userId: string = "";

    @Var(String)
    conversationId: string = "";
}

@Collection({
    namespace: "Audit",
})
export class Audit extends Entity {
    @Var(moment)
    createdAt: moment;

    @Var(moment)
    from: moment;

    @Var(moment)
    to: moment;

    @Var(Number)
    followers?: number;

    @Var(Number)
    unfollowers?: number;

    @Var(Number)
    likes?: number;

    @Var(Number)
    comments?: number;

    @Var(Number)
    urlClicks?: number;

    @Var(Number)
    publications?: number;

    @Var(Number)
    profileViews?: number;

    @Var(Number)
    saved?: number;

    @Var(Number)
    shares?: number;

    @Var(Number)
    totalFollowers?: number;

    @Var(Number)
    totalFollows?: number;

    @Var(String)
    username?: string;

    @Var(String)
    picture?: string;

    @Var(Array.of(MediaAudit))
    posts: MediaAudit[] = [];

    @Var(Array.of(MediaAudit))
    bestPosts?: MediaAudit[] = [];

    @Var(Array.of(MediaAudit))
    worstPosts?: MediaAudit[] = [];

    @Var(PublicationsAudit)
    publicationsAudit?: PublicationsAudit;

    @Var(CommunityAudit)
    communityAudit?: CommunityAudit;

    @Var(ProfileAudit)
    profileAudit?: ProfileAudit;

    @Var(SummaryAudit)
    summary?: SummaryAudit;

    @Var(AIStats)
    lowcostLLM?: AIStats;

    @Var(AIStats)
    highcostLLM?: AIStats;

    @Var(Array.of(ConversationUser))
    conversations: ConversationUser[] = [];

    @Var(AuditCreatingStep)
    step?: AuditCreatingStep;

    get engagementRate(): number {
        if (this.totalFollowers === undefined || this.posts.length === 0) return 0;

        let totalSaves = 0;
        let totalComments = 0;
        let totalLikes = 0;
        let totalShares = 0;

        const coefficients = {
            saves: 0.25,
            comments: 0.25,
            likes: 0.25,
            shares: 0.25,
        };

        this.posts.forEach((post) => {
            totalSaves += post.saved || 0;
            totalComments += post.commentCount || 0;
            totalLikes += post.likes || 0;
            totalShares += post.shares || 0;
        });

        const avgSaves = totalSaves / this.posts.length;
        const avgComments = totalComments / this.posts.length;
        const avgLikes = totalLikes / this.posts.length;
        const avgShares = totalShares / this.posts.length;

        const coefSum = Object.values(coefficients).reduce((acc, coef) => acc + coef, 0);

        const avgRate =
            (avgSaves * coefficients.saves +
                avgComments * coefficients.comments +
                avgLikes * coefficients.likes +
                avgShares * coefficients.shares) /
            coefSum;

        const engagementRate =
            (avgRate * Object.keys(coefficients).length) / this.totalFollowers;
        return engagementRate;
    }

    get engagementRatePercentage(): number {
        return this.engagementRate * 100;
    }

    get averageLikes(): number {
        if (this.posts.length === 0) return 0;
        const avgLikes =
            this.posts.reduce((acc, post) => acc + (post.likes || 0), 0) /
            this.posts.length;
        if (avgLikes < 10) {
            return parseFloat(avgLikes.toFixed(1));
        } else {
            return Math.round(avgLikes);
        }
    }

    get averageComments(): number | string {
        if (this.posts.length === 0) return 0;
        const avgComments =
            this.posts.reduce((acc, post) => acc + (post.commentCount || 0), 0) /
            this.posts.length;
        if (avgComments < 10) {
            return parseFloat(avgComments.toFixed(1));
        } else {
            return Math.round(avgComments);
        }
    }

    get averageSaved(): number | string {
        if (this.posts.length === 0) return 0;
        const avgSaved =
            this.posts.reduce((acc, post) => acc + (post.saved || 0), 0) /
            this.posts.length;
        if (avgSaved < 10) {
            return parseFloat(avgSaved.toFixed(1));
        } else {
            return Math.round(avgSaved);
        }
    }
    get newFollowers(): number {
        return (this.followers || 0) - (this.unfollowers || 0);
    }

    get score(): number {
        const score =
            (this.publicationsAudit?.score || 0) +
            (this.communityAudit?.score || 0) +
            (this.profileAudit?.score || 0);
        return Math.round(score);
    }

    get maxScore(): number {
        return (
            (this.publicationsAudit?.maxScore || 0) +
            (this.communityAudit?.maxScore || 0) +
            (this.profileAudit?.maxScore || 0)
        );
    }

    get scorePercentage(): number {
        return (this.score / this.maxScore) * 100;
    }
}
